import { HANDS, cornerRadiusFor, paintFor, strokeCapsFor, textColor } from './hands.js';
import type { Box, HandId, MotionPlan, Point, ResolvedFigure, SceneShape } from './model.js';

export interface RenderOptions {
  selectedSemanticId?: string | undefined;
  showBounds?: boolean | undefined;
  showAnchors?: boolean | undefined;
  motion?: MotionPlan | undefined;
  selectionTone?: 'default' | 'correct' | 'wrong' | undefined;
}

export function renderSvg(figure: ResolvedFigure, handId: HandId, options: RenderOptions = {}): string {
  const artifactDark = figure.view === 'artifact' && figure.shapes.some((shape) => shape.role === 'ide-dark-gutter' || shape.role === 'ide-dark-canvas');
  const sceneHandId: HandId = figure.view === 'artifact' ? 'kiln-exact' : handId;
  const hand = HANDS[sceneHandId];
  const selected = options.selectedSemanticId;
  const motionById = new Map(options.motion?.instructions.map((item) => [item.objectId, item]));
  const shapes = figure.shapes.map((shape) => renderShape(shape, figure, sceneHandId, selected, motionById.get(shape.id))).join('');
  const hits = figure.hitRegions.map((hit) =>
    `<rect class="hit-region" data-semantic-id="${escapeAttr(hit.semanticId)}" x="${n(hit.box.x)}" y="${n(hit.box.y)}" width="${n(hit.box.width)}" height="${n(hit.box.height)}" fill="transparent" tabindex="0" role="button" aria-label="Inspect ${escapeAttr(hit.semanticId)}"/>`,
  ).join('');
  const bounds = options.showBounds
    ? figure.hitRegions.map((hit) => `<rect class="debug-bound" x="${n(hit.box.x)}" y="${n(hit.box.y)}" width="${n(hit.box.width)}" height="${n(hit.box.height)}"/>`).join('')
    : '';
  const anchors = options.showAnchors
    ? figure.anchors.map((anchor) => `<g class="debug-anchor"><circle cx="${n(anchor.point.x)}" cy="${n(anchor.point.y)}" r="4"/><text x="${n(anchor.point.x + 7)}" y="${n(anchor.point.y - 7)}">${escapeText(anchor.id)}</text></g>`).join('')
    : '';

  return `<svg class="figure-svg hand-${sceneHandId} selection-${options.selectionTone ?? 'default'}" viewBox="0 0 ${n(figure.viewport.width)} ${n(figure.viewport.height)}" role="img" aria-label="${escapeAttr(figure.title)}" data-geometry-hash="${figure.geometryFingerprint}"${options.motion ? ` data-motion-id="${options.motion.id}"` : ''}>
    <defs>${defs()}</defs>
    ${artifactDark || hand.surface === 'plate' ? `<rect width="100%" height="100%" rx="18" fill="${artifactDark ? '#1E1E22' : hand.plate}"/>` : ''}
    <g class="scene">${shapes}</g>
    <g class="debug">${bounds}${anchors}</g>
    <g class="hits">${hits}</g>
  </svg>`;
}

function renderShape(shape: SceneShape, figure: ResolvedFigure, handId: HandId, selected: string | undefined, motion: MotionPlan['instructions'][number] | undefined): string {
  const isSelected = selected === shape.semanticId;
  const active = figure.activeSemanticIds.includes(shape.semanticId) || isSelected;
  const paint = paintFor(handId, shape.role, active);
  const caps = strokeCapsFor(handId);
  const animatedMotion = shape.kind === 'text' && shape.rotate ? undefined : motion;
  const motionAttr = animatedMotion && animatedMotion.kind !== 'stable'
    ? ` data-motion="${animatedMotion.kind}" style="--motion-x:${n(animatedMotion.dx)}px;--motion-y:${n(animatedMotion.dy)}px"`
    : '';
  const common = `data-object-id="${escapeAttr(shape.id)}" data-semantic-id="${escapeAttr(shape.semanticId)}" class="scene-shape${active ? ' is-active' : ''}${isSelected ? ' is-selected' : ''}"${motionAttr}`;
  const shapeDash = shape.kind === 'path' || shape.kind === 'line' ? shape.dash : undefined;
  const dash = paint.dash ?? shapeDash;
  const fill = shape.kind === 'path' || shape.kind === 'line' ? 'none' : paint.fill;
  const style = `fill="${fill}" stroke="${paint.stroke}" stroke-width="${paint.strokeWidth}"${dash ? ` stroke-dasharray="${dash}"` : ''} opacity="${paint.opacity ?? 1}" stroke-linecap="${caps.lineCap}" stroke-linejoin="${caps.lineJoin}"`;
  const marker = active ? 'arrow-brand' : shape.role === 'event' || shape.role === 'pointer' ? 'arrow-blue' : shape.role === 'negative' || shape.role === 'conditional' ? 'arrow-amber' : 'arrow-ink';

  if (shape.kind === 'rect') {
    if (handId === 'teacher-sketch') {
      return `<path ${common} ${style} d="${sketchRect(shape.box, shape.id)}"/>`;
    }
    return `<rect ${common} ${style} x="${n(shape.box.x)}" y="${n(shape.box.y)}" width="${n(shape.box.width)}" height="${n(shape.box.height)}" rx="${n(cornerRadiusFor(handId, shape) ?? 0)}"/>`;
  }
  if (shape.kind === 'polygon') {
    const points = handId === 'teacher-sketch' ? sketchPoints(shape.points, shape.id) : shape.points;
    return `<polygon ${common} ${style} points="${points.map((p) => `${n(p.x)},${n(p.y)}`).join(' ')}"/>`;
  }
  if (shape.kind === 'path') {
    return `<path ${common} ${style} d="${shape.d}"${shape.markerEnd ? ` marker-end="url(#${marker})"` : ''}/>`;
  }
  if (shape.kind === 'line') {
    return `<line ${common} ${style} x1="${n(shape.from.x)}" y1="${n(shape.from.y)}" x2="${n(shape.to.x)}" y2="${n(shape.to.y)}"${shape.markerStart ? ' marker-start="url(#dimension-start)"' : ''}${shape.markerEnd ? ` marker-end="url(#${marker})"` : ''}/>`;
  }
  const transform = shape.rotate ? ` transform="rotate(${shape.rotate} ${n(shape.at.x)} ${n(shape.at.y)})"` : '';
  const letterSpacing = shape.uppercase ? ' letter-spacing="1.1"' : '';
  return `<text ${common} xml:space="preserve" x="${n(shape.at.x)}" y="${n(shape.at.y)}" text-anchor="${shape.align ?? 'start'}" font-family="${shape.mono ? 'Spline Sans Mono, ui-monospace, monospace' : 'Gabarito, system-ui, sans-serif'}" font-size="${n(shape.size)}" font-weight="${shape.weight ?? 500}" fill="${textColor(shape.role)}" stroke="none"${letterSpacing}${transform}>${escapeText(shape.text)}</text>`;
}

function defs(): string {
  return `
    <pattern id="exact-hatch" width="7" height="7" patternTransform="rotate(38)" patternUnits="userSpaceOnUse"><line x1="0" y1="0" x2="0" y2="7" stroke="#1B1B1F" stroke-opacity=".20" stroke-width="1"/></pattern>
    <pattern id="draft-hatch" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse"><line x1="0" y1="0" x2="0" y2="6" stroke="#1B1B1F" stroke-opacity=".27" stroke-width=".75"/></pattern>
    <pattern id="sketch-hatch" width="9" height="9" patternTransform="rotate(41)" patternUnits="userSpaceOnUse"><path d="M0 0 C1 3,-1 6,0 9" fill="none" stroke="#7C3AED" stroke-opacity=".23" stroke-width="1.2"/></pattern>
    ${arrowMarker('arrow-brand', '#7C3AED')}
    ${arrowMarker('arrow-blue', '#2E6BE6')}
    ${arrowMarker('arrow-amber', '#C97700')}
    ${arrowMarker('arrow-ink', '#1B1B1F')}
    <marker id="dimension-start" viewBox="0 0 10 10" refX="1" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M9 1 L1 5 L9 9" fill="none" stroke="#1B1B1F" stroke-width="1"/></marker>`;
}

function arrowMarker(id: string, stroke: string): string {
  return `<marker id="${id}" viewBox="0 0 10 10" refX="8.5" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M1 1 L9 5 L1 9" fill="none" stroke="${stroke}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker>`;
}

function sketchRect(bounds: Box, id: string): string {
  const p = sketchPoints([
    { x: bounds.x, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
    { x: bounds.x, y: bounds.y + bounds.height },
  ], id);
  return `M ${n(p[0]!.x)} ${n(p[0]!.y)} L ${n(p[1]!.x)} ${n(p[1]!.y)} L ${n(p[2]!.x)} ${n(p[2]!.y)} L ${n(p[3]!.x)} ${n(p[3]!.y)} Z`;
}

function sketchPoints(points: Point[], seed: string): Point[] {
  return points.map((p, index) => ({
    x: p.x + jitter(`${seed}:x:${index}`, 0.75),
    y: p.y + jitter(`${seed}:y:${index}`, 0.75),
  }));
}

function jitter(seed: string, amplitude: number): number {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (((hash >>> 0) / 4294967295) * 2 - 1) * amplitude;
}

function n(value: number): string {
  return (Math.round(value * 100) / 100).toString();
}

function escapeText(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
}

function escapeAttr(value: string): string {
  return escapeText(value).replaceAll('"', '&quot;');
}
